import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPaperSearch } from 'js/social/api/paper_search_api';
import { Paper } from '../types';
import { useDebounce } from '../useDebounce';

/**
 * Given an query, returns the associated papers
 * along with whether it's still loading.
 * @param search The query.
 */
export function usePaperSearch(search?: string): {
  isLoading: boolean;
  papers: Paper[];
} {
  const client = useQueryClient();

  const queryKey = ['social-paper-search', search];
  const invalidated = client.getQueryState(queryKey)?.isInvalidated ?? true;
  const { value: debouncedSearch, debouncing } = useDebounce(
    search,
    invalidated ? 500 : 0,
  );
  const {
    data: papers,
    isSuccess: querySuccess,
    isError: queryError,
  } = useQuery({
    enabled: debouncedSearch !== undefined,
    staleTime: 60000,
    queryKey: ['social-paper-search', debouncedSearch],
    queryFn: () => getPaperSearch(debouncedSearch ?? ''),
  });

  return {
    isLoading: debouncing || (!querySuccess && !queryError),
    papers: papers ?? [],
  };
}
